import { api, ApiResponse, PaginatedResponse } from './api';

export interface Category {
  id: string;
  workspace_id: string;
  name: string;
  slug: string;
  color: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Tag {
  id: string;
  workspace_id: string;
  name: string;
  slug: string;
  color: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Assignee {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

export interface ItemDependency {
  id: string;
  title: string;
  board_column: string;
  dependency_id: string;
}

export interface ItemReaction {
  emoji: string;
  count: number;
  user_ids: string[];
}

export interface ItemWatcher {
  id: string;
  name: string;
  avatar?: string;
}

export interface WatchResponse {
  watching: boolean;
  watcher_count: number;
}

export interface Item {
  id: string;
  uuid: string;
  workspace_id: string;
  parent_id?: string;
  title: string;
  description?: string;
  board_column: string;
  board_position: number;
  category_id?: string;
  priority?: string;
  due_date?: string;
  created_by: string;
  assigned_to?: string;
  last_activity_at?: string;
  moved_to_column_at?: string;
  auto_kill_exempt: boolean;
  auto_kill_notified_at?: string;
  vote_count: number;
  user_vote?: number | null;
  comment_count: number;
  status: string;
  item_state?: 'active' | 'blocked' | 'at_risk' | 'on_hold';
  kill_reason?: string;
  metadata?: Record<string, any>;
  external_links?: { url: string; label?: string }[];
  tags?: Tag[];
  assignees?: Assignee[];
  blocks?: ItemDependency[];
  blocked_by?: ItemDependency[];
  reactions?: ItemReaction[];
  watcher_count?: number;
  watchers?: ItemWatcher[];
  created_at: string;
  updated_at: string;
}

export interface CreateItemData {
  title: string;
  description?: string;
  board_column?: string;
  board_position?: number;
  category_id?: string;
  priority?: string;
  due_date?: string;
  assigned_to?: string;
  parent_id?: string;
  assignee_ids?: string[];
  tag_ids?: string[];
  external_links?: { url: string; label?: string }[];
}

export interface UpdateItemData {
  title?: string;
  description?: string;
  category_id?: string;
  priority?: string;
  due_date?: string;
  assigned_to?: string;
  assignee_ids?: string[];
  auto_kill_exempt?: boolean;
  item_state?: 'active' | 'blocked' | 'at_risk' | 'on_hold';
  tag_ids?: string[];
  external_links?: { url: string; label?: string }[];
}

export interface MoveItemData {
  board_column: string;
  board_position?: number;
  kill_reason?: string;
}

export interface ItemFilters {
  column?: string;
  category_id?: string;
  priority?: string;
  assigned_to?: string;
  created_by?: string;
  tag_ids?: string[];
  search?: string;
  sort?: string;
  auto_kill_exempt?: boolean;
  item_state?: string;
}

export const itemApi = {
  getAll: async (workspaceId: string, column?: string): Promise<Item[]> => {
    const params = new URLSearchParams({ all: 'true' });
    if (column) params.append('column', column);
    const response = await api.get<ApiResponse<Item[]>>(`/api/v1/workspaces/${workspaceId}/items?${params.toString()}`);
    return response.data.data || [];
  },

  getPaginated: async (workspaceId: string, page = 1, perPage = 25, filters?: ItemFilters): Promise<PaginatedResponse<Item>> => {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (filters?.column) params.append('column', filters.column);
    if (filters?.category_id) params.append('category_id', filters.category_id);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to);
    if (filters?.created_by) params.append('created_by', filters.created_by);
    if (filters?.tag_ids?.length) params.append('tag_ids', filters.tag_ids.join(','));
    if (filters?.search) params.append('search', filters.search);
    if (filters?.sort) params.append('sort', filters.sort);
    if (filters?.auto_kill_exempt) params.append('auto_kill_exempt', 'true');
    if (filters?.item_state) params.append('item_state', filters.item_state);
    const response = await api.get<ApiResponse<PaginatedResponse<Item>>>(`/api/v1/workspaces/${workspaceId}/items?${params.toString()}`);
    return response.data.data!;
  },

  getById: async (workspaceId: string, id: string): Promise<Item> => {
    const response = await api.get<ApiResponse<Item>>(`/api/v1/workspaces/${workspaceId}/items/${id}`);
    return response.data.data!;
  },

  create: async (workspaceId: string, data: CreateItemData): Promise<Item> => {
    const response = await api.post<ApiResponse<Item>>(`/api/v1/workspaces/${workspaceId}/items`, data);
    return response.data.data!;
  },

  update: async (workspaceId: string, id: string, data: UpdateItemData): Promise<Item> => {
    const response = await api.patch<ApiResponse<Item>>(`/api/v1/workspaces/${workspaceId}/items/${id}`, data);
    return response.data.data!;
  },

  move: async (workspaceId: string, id: string, data: MoveItemData): Promise<Item> => {
    const response = await api.post<ApiResponse<Item>>(`/api/v1/workspaces/${workspaceId}/items/${id}/move`, data);
    return response.data.data!;
  },

  reorder: async (workspaceId: string, items: { id: string; board_position: number }[]): Promise<void> => {
    await api.post(`/api/v1/workspaces/${workspaceId}/items/reorder`, { items });
  },

  restore: async (workspaceId: string, id: string): Promise<Item> => {
    const response = await api.post<ApiResponse<Item>>(`/api/v1/workspaces/${workspaceId}/items/${id}/restore`);
    return response.data.data!;
  },

  delete: async (workspaceId: string, id: string): Promise<void> => {
    await api.delete(`/api/v1/workspaces/${workspaceId}/items/${id}`);
  },

  addDependency: async (workspaceId: string, itemId: string, dependsOnId: string, type: string = 'blocks'): Promise<void> => {
    await api.post(`/api/v1/workspaces/${workspaceId}/items/${itemId}/dependencies`, { depends_on_id: dependsOnId, type });
  },

  removeDependency: async (workspaceId: string, itemId: string, dependencyId: string): Promise<void> => {
    await api.delete(`/api/v1/workspaces/${workspaceId}/items/${itemId}/dependencies/${dependencyId}`);
  },

  react: async (workspaceId: string, itemId: string, emoji: string): Promise<ItemReaction[]> => {
    const response = await api.post<ApiResponse<ItemReaction[]>>(`/api/v1/workspaces/${workspaceId}/items/${itemId}/react`, { emoji });
    return response.data.data || [];
  },

  watch: async (workspaceId: string, itemId: string): Promise<WatchResponse> => {
    const response = await api.post<ApiResponse<WatchResponse>>(`/api/v1/workspaces/${workspaceId}/items/${itemId}/watch`);
    return response.data.data!;
  },

  unwatch: async (workspaceId: string, itemId: string): Promise<WatchResponse> => {
    const response = await api.delete<ApiResponse<WatchResponse>>(`/api/v1/workspaces/${workspaceId}/items/${itemId}/watch`);
    return response.data.data!;
  },

  vote: async (workspaceId: string, itemId: string, direction: 'up' | 'down'): Promise<{ vote_count: number; user_vote: number | null }> => {
    const response = await api.post<ApiResponse<{ vote_count: number; user_vote: number | null }>>(`/api/v1/workspaces/${workspaceId}/items/${itemId}/vote`, { direction });
    return response.data.data!;
  },

  bulkMove: async (workspaceId: string, itemIds: string[], boardColumn: string): Promise<{ moved: number }> => {
    const response = await api.post<ApiResponse<{ moved: number }>>(`/api/v1/workspaces/${workspaceId}/items/bulk-move`, { item_ids: itemIds, board_column: boardColumn });
    return response.data.data!;
  },

  bulkUpdate: async (workspaceId: string, itemIds: string[], data: { category_id?: string; priority?: string; item_state?: string }): Promise<{ updated: number }> => {
    const response = await api.post<ApiResponse<{ updated: number }>>(`/api/v1/workspaces/${workspaceId}/items/bulk-update`, { item_ids: itemIds, ...data });
    return response.data.data!;
  },
};

export const categoryApi = {
  getAll: async (workspaceId: string): Promise<Category[]> => {
    const response = await api.get<ApiResponse<Category[]>>(`/api/v1/workspaces/${workspaceId}/categories`);
    return response.data.data || [];
  },

  create: async (workspaceId: string, data: Partial<Category>): Promise<Category> => {
    const response = await api.post<ApiResponse<Category>>(`/api/v1/workspaces/${workspaceId}/categories`, data);
    return response.data.data!;
  },

  update: async (workspaceId: string, id: string, data: Partial<Category>): Promise<Category> => {
    const response = await api.patch<ApiResponse<Category>>(`/api/v1/workspaces/${workspaceId}/categories/${id}`, data);
    return response.data.data!;
  },

  delete: async (workspaceId: string, id: string): Promise<void> => {
    await api.delete(`/api/v1/workspaces/${workspaceId}/categories/${id}`);
  },
};

export const tagApi = {
  getAll: async (workspaceId: string): Promise<Tag[]> => {
    const response = await api.get<ApiResponse<Tag[]>>(`/api/v1/workspaces/${workspaceId}/tags`);
    return response.data.data || [];
  },

  create: async (workspaceId: string, data: Partial<Tag>): Promise<Tag> => {
    const response = await api.post<ApiResponse<Tag>>(`/api/v1/workspaces/${workspaceId}/tags`, data);
    return response.data.data!;
  },

  update: async (workspaceId: string, id: string, data: Partial<Tag>): Promise<Tag> => {
    const response = await api.patch<ApiResponse<Tag>>(`/api/v1/workspaces/${workspaceId}/tags/${id}`, data);
    return response.data.data!;
  },

  delete: async (workspaceId: string, id: string): Promise<void> => {
    await api.delete(`/api/v1/workspaces/${workspaceId}/tags/${id}`);
  },
};
